import { queryOptions } from '@tanstack/react-query';

import { apiAdminViewsFetch, apiIntroducerDashboardsFetch, apiReferralFetch } from '..';
import {
  ActivitiesResponse,
  AdminActivitiesBody,
  AdminFundFilters,
  AdminFundsResponse,
  AdminIntroducersResponse,
  AdminIntroducerSummary,
  FilterOption,
  FilterTypesRes,
  GetActivitiesParams,
  GetAdminFundsParams,
  GetAdminIntroducersParams,
  GetFilterOptionsParams,
  GetFundsParams,
  GetInvestorsParams,
  IntroducerDashboardFund,
  IntroducerDashboardInvestor,
  IntroducerProfile,
  InvestorEligibility,
  InvestorOwnedEntitiesResponse,
  PaginatedResponse,
} from './types';

export const INTRODUCER_DASHBOARD_QUERY_KEYS = {
  // Own dashboard
  profile: 'introducerProfile',
  filterInvestors: 'introducerFilterInvestors',
  filterFunds: 'introducerFilterFunds',
  filterTypes: 'introducerFilterTypes',
  investors: 'introducerInvestors',
  funds: 'introducerFunds',
  activities: 'introducerActivities',
  // Admin dashboard
  adminSummary: 'adminIntroducerSummary',
  adminFilterInvestors: 'adminIntroducerFilterInvestors',
  adminFilterFunds: 'adminIntroducerFilterFunds',
  adminFilterTypes: 'adminIntroducerFilterTypes',
  adminFundFilters: 'adminIntroducerFundFilters',
  adminIntroducers: 'adminIntroducers',
  adminFunds: 'adminIntroducerFunds',
  adminFundActivities: 'adminIntroducerFundActivities',
  investorEligibility: 'adminIntroducerInvestorEligibility',
  investorOwnedEntities: 'adminIntroducerInvestorOwnedEntities',
};

// ─── Introducer's Own Dashboard ────────────────────────────────────

export const _getIntroducerProfile = (introducerId?: string) => {
  return queryOptions({
    enabled: !!introducerId,
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.profile, introducerId],
    queryFn: () => apiIntroducerDashboardsFetch<IntroducerProfile>(`/${introducerId}/profile`),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getIntroducerFilterInvestors = (
  introducerId?: string,
  params?: GetFilterOptionsParams,
) => {
  return queryOptions({
    enabled: !!introducerId,
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.filterInvestors, introducerId, params?.search],
    queryFn: () =>
      apiIntroducerDashboardsFetch<PaginatedResponse<FilterOption>>(
        `/${introducerId}/filters/investors`,
        { params },
      ),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getIntroducerFilterFunds = (
  introducerId?: string,
  params?: GetFilterOptionsParams,
) => {
  return queryOptions({
    enabled: !!introducerId,
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.filterFunds, introducerId, params?.search],
    queryFn: () =>
      apiIntroducerDashboardsFetch<PaginatedResponse<FilterOption>>(
        `/${introducerId}/filters/funds`,
        { params },
      ),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getIntroducerFilterTypes = (introducerId?: string) => {
  return queryOptions({
    enabled: !!introducerId,
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.filterTypes, introducerId],
    queryFn: () => apiIntroducerDashboardsFetch<FilterTypesRes>(`/${introducerId}/filters/types`),
    refetchOnMount: false,
    retry: 1,
  });
};

export const _getIntroducerInvestors = (introducerId?: string, params?: GetInvestorsParams) => {
  return queryOptions({
    enabled: !!introducerId,
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.investors, introducerId, params],
    queryFn: () =>
      apiIntroducerDashboardsFetch<PaginatedResponse<IntroducerDashboardInvestor>>(
        `/${introducerId}/investors`,
        { params },
      ),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getIntroducerFunds = (introducerId?: string, params?: GetFundsParams) => {
  return queryOptions({
    enabled: !!introducerId,
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.funds, introducerId, params],
    queryFn: () =>
      apiIntroducerDashboardsFetch<PaginatedResponse<IntroducerDashboardFund>>(
        `/${introducerId}/funds`,
        { params },
      ),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getIntroducerActivities = (introducerId?: string, params?: GetActivitiesParams) => {
  return queryOptions({
    enabled: !!introducerId,
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.activities, introducerId, params],
    queryFn: () =>
      apiIntroducerDashboardsFetch<ActivitiesResponse>(`/${introducerId}/activities`, { params }),
    refetchOnMount: true,
    retry: 1,
  });
};

// ─── Admin Dashboard ───────────────────────────────────────────────

export const _getAdminIntroducerSummary = () => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminSummary],
    queryFn: () => apiAdminViewsFetch<AdminIntroducerSummary>('/introducer/summary'),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getAdminIntroducerFilterInvestors = (params?: GetFilterOptionsParams) => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminFilterInvestors, params?.search],
    queryFn: () =>
      apiAdminViewsFetch<PaginatedResponse<FilterOption>>(
        '/introducer/introducers/filters/investors',
        { params },
      ),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getAdminIntroducerFilterFunds = (params?: GetFilterOptionsParams) => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminFilterFunds, params?.search],
    queryFn: () =>
      apiAdminViewsFetch<PaginatedResponse<FilterOption>>('/introducer/introducers/filters/funds', {
        params,
      }),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getAdminIntroducerFilterTypes = () => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminFilterTypes],
    queryFn: () => apiAdminViewsFetch<FilterTypesRes>('/introducer/introducers/filters/types'),
    refetchOnMount: false,
    retry: 1,
  });
};

export const _getAdminFundFilters = () => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminFundFilters],
    queryFn: () => apiAdminViewsFetch<AdminFundFilters>('/introducer/funds/filters'),
    refetchOnMount: false,
    retry: 1,
  });
};

export const _getAdminIntroducers = (params?: GetAdminIntroducersParams) => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminIntroducers, params],
    queryFn: () =>
      apiAdminViewsFetch<AdminIntroducersResponse>('/introducer/introducers', { params }),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getAdminIntroducerFunds = (params?: GetAdminFundsParams) => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminFunds, params],
    queryFn: () => apiAdminViewsFetch<AdminFundsResponse>('/introducer/funds', { params }),
    refetchOnMount: true,
    retry: 1,
  });
};

export const _getAdminFundActivities = (body: AdminActivitiesBody) => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.adminFundActivities, body],
    queryFn: () =>
      apiAdminViewsFetch<ActivitiesResponse>('/introducer/activities', { method: 'POST', body }),
    enabled: !!(body.fundId || body.introducerUserId || body.investorUserId),
    refetchOnMount: true,
    retry: 1,
  });
};

// ─── Investor Eligibility ────────────────────────────────────────────

export const _getInvestorEligibility = (userId: string | undefined, fundId?: string) => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.investorEligibility, userId, fundId],
    queryFn: () =>
      apiReferralFetch<InvestorEligibility>(`/internal/investor/${userId}/eligibility`, {
        params: fundId ? { fundId } : undefined,
      }),
    enabled: !!userId,
    retry: 1,
  });
};

export const _getInvestorOwnedEntities = (investorUserId: string | undefined) => {
  return queryOptions({
    queryKey: [INTRODUCER_DASHBOARD_QUERY_KEYS.investorOwnedEntities, investorUserId],
    queryFn: () =>
      apiAdminViewsFetch<InvestorOwnedEntitiesResponse>(
        `/introducer/investors/${investorUserId}/owned-entities`,
      ),
    enabled: !!investorUserId,
    retry: 1,
  });
};
